import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatNumber } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.apartmentProduct;
const queryKey = "apartment";

async function fetchApartment(currentPage, search, projectId, apartmentTypeId) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (projectId) options.projectId = projectId;
    if (apartmentTypeId) options.apartmentTypeId = apartmentTypeId;

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/apartment?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        isCanDelete: item?.isCanDelete || false,
        code: item?.code || "-",
        name: item?.name || "-",
        area: item?.area ? `${item?.area} m2` : "-",
        areaOfWater: item?.areaOfWater ? `${formatNumber(item?.areaOfWater)} m2` : "-",
        builtUpArea: item?.builtUpArea ? `${formatNumber(item?.builtUpArea)} m2` : "-",
        grossFloorArea: item?.grossFloorArea ? `${formatNumber(item?.grossFloorArea)} m2` : "-",
        width: item?.width ? `${formatNumber(item?.width)} m` : "-",
        length: item?.length ? `${formatNumber(item?.length)} m` : "-",
        numberOfBedrooms: item?.numberOfBedrooms || "-",
        numberOfToilets: item?.numberOfToilets || "-",
        direction: item?.direction?.name || "-",
        apartmentType: item?.apartmentType?.name || "-",
        property: item?.property?.name || "-",
        project: item?.project?.name || "-",
        division: item?.division?.name || "-",
        totalValue: item?.totalValue
          ? `${formatNumber(item?.totalValue)} VNĐ`
          : "0 VNĐ",
        priceForCommission: item?.priceForCommission
          ? `${formatNumber(item?.priceForCommission)} VNĐ`
          : "0 VNĐ",
        estimatedServiceFeePrice: item?.estimatedServiceFeePrice
          ? `${formatNumber(item?.estimatedServiceFeePrice)} VNĐ`
          : "0 VNĐ",
        edit: {
          id: item?._id || "",
          code: item?.code || "",
          name: item?.name || "",
          area: item?.area || "",
          builtUpArea: item?.builtUpArea || "",
          width: item?.width || "",
          length: item?.length || "",
          numberOfBedrooms: item?.numberOfBedrooms || "",
          numberOfToilets: item?.numberOfToilets || "",
          directionId: item?.directionId || "",
          apartmentTypeId: item?.apartmentTypeId || "",
          propertyId: item?.propertyId || "",
          projectId: item?.projectId || "",
          divisionId: item?.divisionId || "",
          totalValue: item?.totalValue || 0,
          priceForCommission: item?.priceForCommission || 0,
          estimatedServiceFeePrice: item?.estimatedServiceFeePrice || 0,
          areaOfWater: item?.areaOfWater || "",
          grossFloorArea: item?.grossFloorArea || "",
        },
        detail: {
          id: item?._id || "",
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useApartmentTable(search, projectId, apartmentTypeId) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [queryKey, currentPage, search, projectId, apartmentTypeId],
    queryFn: () =>
      fetchApartment(currentPage, search, projectId, apartmentTypeId),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    apartmentData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
